import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { combineLatest } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { MaterialModule } from 'src/app/core/material.module';
import { AbilityService } from 'src/app/core/services/ability/ability.service';
import { AppService } from 'src/app/core/services/app.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { StorageBrowser } from 'src/app/core/services/storage.service';
import { UserService } from 'src/app/core/services/user.service';
import { DevicesSelectorComponent } from 'src/app/features/devices/components/devices-selector/devices-selector.component';
import { handleDeviceAssignmentError } from 'src/app/features/devices/helpers/device-error.helper';
import { handleTestsPerOpeningError } from 'src/app/features/tests/features/helpers/tests-per-opening-error.helper';
import { DeviceService } from 'src/app/features/devices/services/devices.service';
import { ResearchConsentService } from 'src/app/features/research-consent/services/research-consent.service';
import { DirectConnectionStatus, PatientType } from 'src/app/features/dormotech-direct/dormotech-direct.types';
import { DormotechDirectService } from 'src/app/features/dormotech-direct/services/dormotech-direct.service';
import { GenderEnum, RolesEnum } from 'src/app/features/tests/features/constants';
import { isTitrationTemplate } from 'src/app/features/working-area/components/working-area2/types/signal-types';
import {
  mapPatientSaveError,
  PATIENT_STEP_LABELS,
  PatientFormStep,
} from '../../services/patient-save-errors';
import { PatientsService } from '../../services/patients.service';
import { PatientAddCommentComponent } from './components/patient-add-comment/patient-add-comment.component';
import { PatientAddInfoComponent } from './components/patient-add-info/patient-add-info.component';
import { PatientMainInfoComponent } from './components/patient-main-info/patient-main-info.component';
/** Wizard steps in tab order, mapped to the step names the API errors carry. */
const STEP_ORDER: PatientFormStep[] = [
  'mainInfo',
  'additionalInfo',
  'comment',
  'device',
];
const STEPS: { id: string; valid: boolean }[] = [
  { id: 'app-patient-main-info', valid: false },
  { id: 'app-patient-add-info', valid: false },
  { id: 'app-patient-add-comment', valid: false },
  { id: 'app-devices-selector', valid: false },
];
@Component({
  selector: 'app-new-patient-dialog',
  templateUrl: './new-patient-dialog.component.html',
  styleUrls: ['./new-patient-dialog.component.scss'],
  imports: [
    CommonModule,
    MaterialModule,
    PatientMainInfoComponent,
    PatientAddInfoComponent,
    PatientAddCommentComponent,
    DevicesSelectorComponent,
  ],
})
export class NewPatientDialogComponent implements OnInit {
  @ViewChild(PatientAddInfoComponent) private addInfo?: PatientAddInfoComponent;
  public currentStep = 0;
  public completedSteps = STEPS.map((m) => ({ ...m }));
  loading = false;
  data: any;
  userMainData: any;
  mainInfoData: any;
  addInfoData: any;
  commentData: any;
  selectedDevice: any;

  /** Gates the "Send IRB form" option; follows the PATIENT's department. */
  public researchConsentEnabled = false;

  /** Gates the Clinic / Direct choice; follows the PATIENT's department, like the IRB option. */
  public directEnabled = false;
  /**
   * Why a department with Direct on is not offered it right now — this lab's
   * connection to Dormotech Direct is not working. Null otherwise.
   */
  public directUnavailable: string | null = null;
  /** Clinic, or Direct — handed to Dormo Direct, which assigns and ships the device. */
  public patientType: PatientType = 'CLINIC';
  /**
   * A Direct patient has no Assign Device step, so Comments is the last one and
   * saving starts there: the comment is collected first, and the save follows.
   * Holds the "add another patient" choice while that happens.
   */
  private saveAfterComment: boolean | null = null;

  /**
   * Field errors the API rejected the last save with, per step. Passed down so the
   * offending field turns red and says why, instead of the user matching a toast
   * against four tabs of form.
   */
  public serverErrors: Partial<Record<PatientFormStep, Record<string, string>>> = {};
  /** The patient's department, as the department step currently holds it. */
  private patientDepartmentId?: string;


  gender = [
    { viewValue: 'Male', value: GenderEnum.MALE },
    { viewValue: 'Female', value:GenderEnum.FEMALE },
  ];
  boolChoose = [
    { viewValue: 'Yes', value: true },
    { viewValue: 'No', value: false },
  ];
  active = [
    { value: true, viewValue: 'Active' },
    { value: false, viewValue: 'Inactive' },
  ];

  constructor(
    public ability: AbilityService,
    private storageBrowser: StorageBrowser,
    private appService: AppService,
    private patientsService: PatientsService,
    private devicesService: DeviceService,

    public route: ActivatedRoute,
    public userService: UserService,
    private notificationService: NotificationService,
    public router: Router,
    private researchConsentService: ResearchConsentService,
    private dialogRef: MatDialogRef<NewPatientDialogComponent>,
    private dormotechDirect: DormotechDirectService
  ) {}

  public get isDirect(): boolean {
    return this.patientType === 'DIRECT';
  }

  /** The step that saves: Assign Device, or Comments for a Direct patient. */
  public get lastStep(): number {
    return this.isDirect ? 2 : 3;
  }

  /** Main Info switched between Clinic and Direct. */
  public onPatientTypeChange(patientType: PatientType) {
    if (patientType === this.patientType) {
      return;
    }
    this.patientType = patientType;
    // The address the next step asks for, and whether it must be validated,
    // differ between the two: it has to be gone through again.
    this.completedSteps[1].valid = false;
    this.completedSteps[2].valid = false;
    this.completedSteps[3].valid = false;
  }

  ngOnInit(): void {
    // Whether Direct is offered is read afresh on every open: the department's
    // switch and the lab's connection may have changed since the last one.
    if (this.ability.canUseDormotechDirect()) {
      this.dormotechDirect.forgetAnswers();
    }
    // Nothing to resolve yet on purpose. The research consent option follows the
    // PATIENT's department, which the department step publishes as soon as it
    // knows it — immediately for a user scoped to one department, and on selection
    // for an admin who works across several. Seeding this from the signed-in
    // user's own department instead was wrong for exactly that second case: they
    // create patients in departments they do not belong to, and the option would
    // have reflected their department rather than the patient's.
  }

  /**
   * The patient's department was set or changed on the department step.
   *
   * Re-resolved rather than cached once, because a user who can see several
   * departments gets a different answer for each, and the option must reflect the
   * department the patient is actually being created in.
   */
  public onPatientDepartmentChange(departmentId: string | null) {
    if (!departmentId) {
      this.patientDepartmentId = undefined;
      this.researchConsentEnabled = false;
      this.directEnabled = false;
      this.directUnavailable = null;
      return;
    }
    if (this.patientDepartmentId === departmentId) {
      return;
    }
    this.patientDepartmentId = departmentId;
    if (this.ability.canUseDormotechDirect()) {
      // Offered only when the department has Direct on and this lab is
      // connected to Dormotech Direct: without a connection nothing could be sent.
      combineLatest([
        this.dormotechDirect.isEnabledForDepartment(departmentId),
        this.dormotechDirect.connectionStatus(),
      ]).subscribe(([enabled, connection]) => {
        if (this.patientDepartmentId === departmentId) {
          this.directEnabled = enabled && connection.connected;
          this.directUnavailable = enabled && !connection.connected ? directUnavailableMessage(connection) : null;
        }
      });
    } else {
      this.directEnabled = false;
      this.directUnavailable = null;
    }
    this.researchConsentService
      .isEnabledForDepartment(departmentId)
      .subscribe((enabled) => {
        // Guard against a slow answer for a department the user has since moved
        // away from landing on top of a newer one.
        if (this.patientDepartmentId === departmentId) {
          this.researchConsentEnabled = enabled;
        }
      });
  }

  onClose() {
    this.dialogRef.close();
  }
  onStepChange(index: number) {
    // Leaving the address step by its tab header skips "Continue", which is
    // where the address is checked — so check it here too.
    if (this.currentStep === 1 && index !== 1) {
      this.addInfo?.checkShippingAddress();
    }
    this.currentStep = index;
  }

  isStepValid(index: number) {
    return this.completedSteps[index]?.valid == true;
  }

  canAdvanceToStep(stepIndex: number): boolean {
    const firstInvalidStepIndex = this.completedSteps.findIndex(
      (step) => !step.valid
    );
    if (firstInvalidStepIndex == -1) {
      // all valid
      return true;
    }
    if (stepIndex <= firstInvalidStepIndex) {
      return true;
    }
    return false;
  }

  continueToNextStep(addOtherPatient = false) {
    if (this.isDirect && this.currentStep === this.lastStep) {
      this.saveAfterComment = addOtherPatient;
      this.patientsService.eventEmitter.emit('app-patient-add-comment');
      return;
    }
    if (this.currentStep >= 3) {
      this.save(addOtherPatient);
      return;
    }

    if (this.currentStep == 0) {
      this.patientsService.eventEmitter.emit('app-patient-main-info');
      return;
    }
    if (this.currentStep == 1) {
      this.patientsService.eventEmitter.emit('app-patient-add-info');
      return;
    }
    if (this.currentStep == 2) {
      this.patientsService.eventEmitter.emit('app-patient-add-comment');
      return;
    }
    if (this.currentStep == 3) {
      this.patientsService.eventEmitter.emit('app-devices-selector');
      return;
    }
  }

  public resetForm() {
    this.clearServerErrors();
    this.saveAfterComment = null;
    this.data = {};
    this.mainInfoData = {};
    this.commentData = {};
    this.addInfoData = {};
    this.selectedDevice = {};
    this.completedSteps = STEPS.map((m) => ({ ...m }));
    this.currentStep = 0;
    this.patientsService.eventEmitter.emit('reset');
    this.devicesService.eventEmitter.emit('reset');
  }

  public save(addOtherPatient) {
    const currUser = this.userService.currentUser.getValue();
    let technicianId = null;
    if ([RolesEnum.Technician].includes(currUser.role)) {
      technicianId = currUser.id;
    }
    let data = {
      // A Direct patient's device is assigned by Dormo Direct.
      deviceId: this.isDirect ? undefined : this.selectedDevice?.id,
      patientType: this.patientType,
      ...this.mainInfoData,
      ...this.addInfoData,
      ...this.commentData,
      withTest: true,
      technicianId,
      // Re-checked here rather than where the tick was captured. The option is
      // ticked on the first step but the patient's department is chosen on a later
      // one, so a tick made while an eligible department was selected could
      // otherwise survive a switch to one that does not run the programme — and the
      // backend would refuse the whole save over it.
      sendResearchConsent:
        this.researchConsentEnabled && !!this.mainInfoData?.sendResearchConsent,
      user: {
        ...this.userMainData,
      },
    };
    this.loading = true;
    this.clearServerErrors();
    this.patientsService.createPatient(data).subscribe(
      (res) => {
        const patientId = res.patientId;
        this.announceCreated(res);
        if (addOtherPatient) {
          this.resetForm();
        } else {
          this.onClose();
        }
        this.appService.eventEmitter.emit('TESTS:NEW_TEST_CREATED');
        this.loading = false;
      },
      (error) => {
        this.loading = false;
        if (handleDeviceAssignmentError(error, this.notificationService)) {
          return;
        }
        if (handleTestsPerOpeningError(error, this.notificationService)) {
          return;
        }
        // Anything the API named a field for is shown ON that field, on the step
        // it lives on, with the wizard moved there. The toast stays as well: the
        // user may be looking at a different step when the save comes back.
        if (this.showServerFieldError(error)) {
          return;
        }
        if (this.showDirectSaveError(error)) {
          return;
        }
        // Patient ID, email and the consent-email error are all handled above,
        // on their own fields.
        if (
          error.error.message ===
          'RESEARCH_CONSENT.ERRORS.NOT_ENABLED_FOR_DEPARTMENT'
        ) {
          this.notificationService.error(
            'The IRB form is not enabled for this department'
          );
        } else if (error.error.name === 'AccessDeniedError') {
          this.notificationService.error(
            'You are not eligible to create test '
          );
        } else {
          console.error({ error });
          this.notificationService.error('Cannot create create test ');
        }
      }
    );
  }

  getForm({ valid, form, value }) {
    if (form == 'app-patient-main-info') {
      this.completedSteps[0].valid = valid;
      if (valid) {
        this.currentStep++;
        if (value) {
          const dob =
            value.dob instanceof Date
              ? moment(value.dob).format('YYYY-MM-DD')
              : value.dob;
          this.mainInfoData = {
            middleName: value.middleName,
            patientId: value.patientId,
            height: Number(value.height),
            weight: value.weight ? Number(value.weight) : null,
            birthdayDate: dob,
            gender: value.gender,
            age: parseInt(value.age),
            location: value.location,
            roomId: value.roomId,
            scheduleDateTime: value.scheduleDateTime
              ? value.scheduleDateTime
              : null,
            testType: value.testType,
            // Only a titration names the device it is run on; any other study
            // leaves it empty even if one was picked before the type changed.
            titrationType: isTitrationTemplate(value.testType)
              ? value.titrationType || null
              : null,
            testsCount: value.testsCount,
            hasExternalFile: value.hasExternalFile,
            isPediatric: !!value.isPediatric,
            sendResearchConsent: !!value.sendResearchConsent,
            interpretingPhysicianId: value.interpretingPhysicianId,
          };
          this.userMainData = {
            // Blank means "no address on file", and the API expresses that as an
            // absent value: it validates the address whenever the key carries a
            // string, so an empty one is read as a malformed address and the whole
            // save is refused. A patient with no email is otherwise perfectly
            // legal, which is why the field is not required to begin with.
            email: value.email?.trim() ? value.email.trim() : null,
            phone: value.phone,
            firstName: value.firstName,
            lastName: value.lastName,
            isActive: value.isActive,
            departmentId: value.departmentId,
          };
          this.onPatientDepartmentChange(value.departmentId);
        }
      }
    }
    if (form == 'app-patient-add-info') {
      this.completedSteps[1].valid = valid;
      if (valid) {
        this.currentStep++;
        if (value) {
          this.addInfoData = {
            insurer: value.insurer,
            shippingAddress: value.shippingAddress,
            epworthSleepinessScale: value.epworthSleepinessScale,
            smoker: value.smoker,
            sleepDisorder: value.sleepDisorder,
            race: value.race,
            alcohol: value.alcohol,
            history: value.history,
            medications: value.medications,
            caffeine: value.caffeine,
            exercise: value.exercise,
            stressLevel: value.stressLevel,
            medicationGroups: (value.medicationGroups || []).join(',') || '',
            profession: value.profession,

            countryOfBirth: value.countryOfBirth,
            education: value.education,
            incomeLevel: value.incomeLevel,

            deviceSerial: value.deviceSerial,

            isPregnant: value.isPregnant == '' ? null : value.isPregnant,
            generic1: value.generic_1,
            generic2: value.generic_2,
            generic3: value.generic_3,
            gestationalAge: value.gestationalAge ? value.gestationalAge : null,
            medicalCondition: value.medicalCondition,
            referringDoctorId: value.referringDoctorId,
            shiftWork: value.shiftWork,
            neckSize: value.neckSize,
            orderingPhysicianId: value.orderingPhysicianId,
          };

        }
      }
    }

    if (form == 'app-patient-add-comment') {
      this.completedSteps[2].valid = valid;
      const saveAfterComment = this.saveAfterComment;
      this.saveAfterComment = null;
      if (valid) {
        this.commentData = value;
        if (saveAfterComment !== null) {
          this.save(saveAfterComment);
        } else {
          this.currentStep++;
        }
      }
    }

    if (form == 'app-devices-selector') {
      this.completedSteps[3].valid = valid;
      if (valid) {
        this.currentStep = 3;
        this.selectedDevice = value;
      }
    }
  }

  /**
   * Put a rejected save on the field it was about.
   *
   * Returns false when the API named something no field on this form covers, so
   * the caller falls back to its message. Guessing a field for an unrecognised
   * error would mark one that is fine, which is worse than a message alone.
   */
  private showServerFieldError(error: any): boolean {
    const mapped = mapPatientSaveError(error);
    if (!mapped) {
      return false;
    }

    // A new object each time: the child applies these in ngOnChanges, which only
    // runs when the reference changes.
    this.serverErrors = {
      ...this.serverErrors,
      [mapped.step]: { [mapped.control]: mapped.message },
    };

    // Flag the step so the tab shows it, and move there — the save was very likely
    // triggered from the last step, a long way from the field at fault.
    const stepIndex = STEP_ORDER.indexOf(mapped.step);
    if (stepIndex !== -1) {
      this.completedSteps[stepIndex].valid = false;
      this.currentStep = stepIndex;
    }

    this.notificationService.error(
      `${mapped.toast} (${PATIENT_STEP_LABELS[mapped.step]})`
    );
    return true;
  }

  /** What became of a new patient — for a Direct one, whether Dormo Direct got them. */
  private announceCreated(res: any) {
    if (res?.directStatus === 'TRANSFER_PENDING') {
      // Dormotech Direct accepts at once and creates the patient afterwards.
      this.notificationService.success('Patient created. Sending to Dormotech Direct — the Status column shows when it is done.');
    } else if (res?.directStatus === 'TRANSFERRED') {
      this.notificationService.success('Patient created and transferred to Dormotech Direct');
    } else if (res?.directStatus === 'TRANSFER_FAILED') {
      this.notificationService.error('Patient created, but the transfer to Dormotech Direct failed');
    } else {
      this.notificationService.success('Patient Created Successfully');
    }
  }

  /**
   * A Direct patient refused before anything was saved: the department, the
   * contact details or the address. Moves to the step the problem is on.
   */
  private showDirectSaveError(error: any): boolean {
    const body = error?.error ?? {};
    const atStep = (step: number, message: string) => {
      this.completedSteps[step].valid = false;
      this.currentStep = step;
      this.notificationService.error(message);
      return true;
    };
    switch (body.message) {
      case 'DORMOTECH_DIRECT.ERRORS.NOT_ENABLED_FOR_DEPARTMENT':
        return atStep(0, 'Dormotech Direct is not enabled for this department');
      case 'DORMOTECH_DIRECT.ERRORS.NOT_AVAILABLE':
        return atStep(0, 'Dormotech Direct is not available on this lab');
      case 'DORMOTECH_DIRECT.ERRORS.CONTACT_REQUIRED':
        return atStep(0, 'A Direct patient needs an email and a phone');
      case 'DORMOTECH_DIRECT.ERRORS.NOT_CONNECTED':
        // The choice goes with the connection: the note on the first step says why.
        this.directEnabled = false;
        this.directUnavailable = directUnavailableMessage({ state: 'REJECTED' });
        return atStep(0, 'This patient could not be sent: Dormotech Direct no longer accepts this lab\'s secret');
    }
    if (body.name === 'DirectAddressRefusedError' || body.name === 'DirectAddressNotCheckedError') {
      return atStep(1, body.message || 'The address could not be validated');
    }
    return false;
  }

  /** True when the last save was rejected because of a field on this step. */
  public stepHasError(index: number): boolean {
    const step = STEP_ORDER[index];
    return !!step && !!this.serverErrors[step];
  }

  /** Drop the previous rejection before a fresh attempt. */
  private clearServerErrors() {
    this.serverErrors = {};
  }
}

/** Why Direct is not offered, and who can change that. */
function directUnavailableMessage(connection: Pick<DirectConnectionStatus, 'state'>): string {
  switch (connection.state) {
    case 'REJECTED':
      return 'Direct patients can\'t be created right now: Dormotech Direct no longer accepts this lab\'s secret. '
        + 'An admin can enter a new one in Department Settings, under Direct Enabled.';
    case 'UNKNOWN':
      return 'Direct patients can\'t be offered right now: the connection to Dormotech Direct could not be checked. '
        + 'Close New Patient and open it again to retry.';
    default:
      return 'Direct patients can\'t be created right now: this lab isn\'t connected to Dormotech Direct. '
        + 'An admin can connect it in Department Settings, under Direct Enabled.';
  }
}
